import React, { useMemo } from "react";
import { Box, Container, Grid, Heading, Text, Link, Skeleton } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuth } from "../../contexts/AuthContext";
import { Candidate, CandidateForm, CandidateStage, Client } from "../../generated/graphql";
import { GET_CANDIDATE_OVERVIEW, GET_CANDIDATE_FORM_STATES } from "../prospecting/queries";
import { canonicalStage, isExitStage, stageIndex, stageLabel } from "../prospecting/utils/stages";
import { GET_EMPLOYEES_PAGINATED } from "../employees/queries";
import { Blueprint } from "./Blueprint";
import { Kicker } from "./Kicker";
import { Tag, TagVariant } from "./Tag";
import { IndustryButton } from "./IndustryButton";
import { LABEL_STYLE, useIndustry } from "./tokens";
import { useModuleAccess } from "./useModuleAccess";
import { PANEL_ROW_LIMIT, PanelSpec, StatKey, panelSpecs, statSpecs, workspaceLinks } from "./homeContent";

// ---------- Query row shapes (Pick of generated entity types — never local duplicates) ----------

type CandidateRow = Pick<Candidate, "id" | "firstName" | "lastName" | "jobTitle" | "stage" | "status" | "createdAt" | "updatedAt">;
type FormRow = Pick<CandidateForm, "id" | "name" | "jobTitle" | "employerName" | "slug" | "isActive">;
interface CandidateOverviewData {
  candidates: CandidateRow[];
}
interface FormStatesData {
  candidateForms: FormRow[];
}
interface EmployeeTotalData {
  employeesPaginated: { total: number };
}

type Viewer = Pick<Client, "fName" | "lName" | "email"> | null;

// ---------- Presentation helpers ----------

// Stage labels come from the prospecting module's single map (legacy aliases → canonical ladder).

const stageTag = (stage: CandidateStage): TagVariant => {
  if (isExitStage(stage)) return "outline";
  if (stageIndex(stage) >= stageIndex(CandidateStage.InterviewScheduled)) return "accent";
  return "neutral";
};

// Dates are stored UTC; the back office reads them Brisbane-anchored (date-standards golden rule).
const BRISBANE = "Australia/Brisbane";
const timeFmt = new Intl.DateTimeFormat("en-AU", { timeZone: BRISBANE, hour: "2-digit", minute: "2-digit", hour12: false });
const dayFmt = new Intl.DateTimeFormat("en-AU", { timeZone: BRISBANE, day: "numeric", month: "short" });
const dayKey = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: BRISBANE, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

const whenLabel = (iso: string, now: Date): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return dayKey(d) === dayKey(now) ? timeFmt.format(d) : `${dayFmt.format(d)} ${timeFmt.format(d)}`;
};

const candidateName = (c: CandidateRow) => `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "Unnamed candidate";

// ---------- Small building blocks ----------

const PanelHeader: React.FC<{ spec: PanelSpec; right?: React.ReactNode }> = ({ spec, right }) => {
  const t = useIndustry();
  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={3}
      justifyContent="space-between"
      alignItems="center"
      px={5}
      py={3}
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor={t.rule}
    >
      <Heading as="h2" fontSize="16px" lineHeight={1} fontWeight={600}>
        {spec.title}
      </Heading>
      <Box display="flex" alignItems="center" gap={3}>
        {right}
        <Link as={RouterLink} to={spec.to} title={spec.ctaTitle} fontSize="13px" color={t.accent700} _hover={{ color: t.steel, textDecoration: "none" }}>
          {spec.ctaLabel}
        </Link>
      </Box>
    </Box>
  );
};

const PanelDescription: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const t = useIndustry();
  return (
    <Text fontSize="13px" lineHeight="20px" color={t.muted} px={5} pt={3}>
      {children}
    </Text>
  );
};

/** Loading / error / empty are three different truths (hardening rule 14). */
const PanelState: React.FC<{ loading: boolean; error?: Error; empty: boolean; emptyText: string }> = ({ loading, error, empty, emptyText }) => {
  const t = useIndustry();
  if (loading) {
    return (
      <Box px={5} py={4} display="flex" flexDirection="column" gap={3} title="Loading">
        <Skeleton h="14px" borderRadius={0} startColor={t.surface} endColor={t.ruleLight} />
        <Skeleton h="14px" borderRadius={0} startColor={t.surface} endColor={t.ruleLight} w="70%" />
      </Box>
    );
  }
  if (error) {
    return (
      <Text px={5} py={4} fontSize="13px" color={t.text} title={error.message}>
        Couldn’t load this panel. {error.message}
      </Text>
    );
  }
  if (empty) {
    return (
      <Text px={5} py={4} fontSize="13px" color={t.muted}>
        {emptyText}
      </Text>
    );
  }
  return null;
};

const TruncationNote: React.FC<{ shown: number; total: number; to: string; title: string }> = ({ shown, total, to, title }) => {
  const t = useIndustry();
  if (total <= shown) return null;
  return (
    <Text px={5} py={3} fontSize="12px" color={t.muted}>
      Showing {shown} of {total} ·{" "}
      <Link as={RouterLink} to={to} title={title} color={t.accent700}>
        see all
      </Link>
    </Text>
  );
};

const ColumnHead: React.FC<{ children: React.ReactNode; align?: "left" | "right" }> = ({ children, align = "left" }) => {
  const t = useIndustry();
  return (
    <Text as="span" {...LABEL_STYLE} fontWeight={600} color={t.faint} textAlign={align}>
      {children}
    </Text>
  );
};

// ---------- The dashboard ----------

/**
 * Authenticated Home — the artboard's dashboard layout, populated honestly:
 * the KPI strip and panels read the Prospecting + Employees modules that own
 * each job; cells with no data model in this app show a dash and say so.
 */
export const BackOfficeView: React.FC = () => {
  const t = useIndustry();
  const { user } = useAuth();
  const viewer = user as Viewer;
  const { canView } = useModuleAccess();
  const canProspecting = canView("prospecting");
  const canEmployees = canView("employees");

  const candidatesQ = useQuery<CandidateOverviewData>(GET_CANDIDATE_OVERVIEW, { skip: !canProspecting, fetchPolicy: "cache-and-network" });
  const formsQ = useQuery<FormStatesData>(GET_CANDIDATE_FORM_STATES, { skip: !canProspecting, fetchPolicy: "cache-and-network" });
  const employeesQ = useQuery<EmployeeTotalData>(GET_EMPLOYEES_PAGINATED, {
    variables: { input: { offset: 0, limit: 1 } },
    skip: !canEmployees,
    fetchPolicy: "cache-and-network",
  });

  const now = useMemo(() => new Date(), []);
  const candidates = useMemo(() => candidatesQ.data?.candidates ?? [], [candidatesQ.data]);
  const forms = useMemo(() => formsQ.data?.candidateForms ?? [], [formsQ.data]);

  const derived = useMemo(() => {
    const weekAgo = now.getTime() - 7 * 24 * 60 * 60 * 1000;
    const byUpdated = [...candidates].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    const shortlisted = byUpdated.filter((c) => canonicalStage(c.stage) === CandidateStage.Suitable);
    const activeForms = forms.filter((f) => f.isActive);
    const formsSorted = [...forms].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
    const stats: Record<StatKey, number | null> = {
      openRoles: activeForms.length,
      applicantsWeek: candidates.filter((c) => new Date(c.createdAt).getTime() >= weekAgo).length,
      passedKnockouts: candidates.filter((c) => stageIndex(c.stage) >= stageIndex(CandidateStage.InterviewScheduled)).length,
      checksAwaiting: null,
      shortlistsReady: shortlisted.length,
    };
    return { byUpdated, shortlisted, activeForms, formsSorted, stats };
  }, [candidates, forms, now]);

  const statState = (source: "forms" | "candidates" | "none") => {
    if (source === "forms") return { hidden: !canProspecting, loading: formsQ.loading && !formsQ.data, error: formsQ.error };
    if (source === "candidates") return { hidden: !canProspecting, loading: candidatesQ.loading && !candidatesQ.data, error: candidatesQ.error };
    return { hidden: false, loading: false, error: undefined };
  };

  const visibleStats = statSpecs.filter((s) => !statState(s.source).hidden);
  const viewerName = viewer ? `${viewer.fName ?? ""} ${viewer.lName ?? ""}`.trim() || viewer.email || "" : "";
  const visibleLinks = workspaceLinks.filter((l) => canView(l.moduleId));
  const employeesTotal = employeesQ.data?.employeesPaginated?.total;

  return (
    <Box as="main" bg={t.ground} color={t.text}>
      <Container maxW="1200px" px={{ base: 5, md: 10 }} py={{ base: 8, md: 12 }} display="flex" flexDirection="column" gap={{ base: 8, md: 10 }}>
        {/* Page head */}
        <Box>
          <Kicker mb={6}>Back office</Kicker>
          <Box display="flex" flexWrap="wrap" alignItems="baseline" justifyContent="space-between" gap={3}>
            <Heading as="h1" fontSize="clamp(30px, 3.2vw, 42px)" lineHeight={1.08} fontWeight={600}>
              Overview
            </Heading>
            {viewerName && (
              <Text fontSize="13px" color={t.muted} title="The account you are signed in with">
                Signed in as {viewerName}
              </Text>
            )}
          </Box>
        </Box>

        {/* KPI strip */}
        {visibleStats.length > 0 && (
          <Grid
            as="section"
            aria-label="Key figures"
            templateColumns="repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
            borderWidth="1px"
            borderStyle="solid"
            borderColor={t.rule}
            sx={{ "& > *": { borderRightWidth: "1px", borderRightStyle: "solid", borderRightColor: t.rule, borderBottomWidth: "1px", borderBottomStyle: "solid", borderBottomColor: t.rule, mr: "-1px", mb: "-1px" } }}
          >
            {visibleStats.map((s) => {
              const st = statState(s.source);
              const value = derived.stats[s.key];
              return (
                <Box key={s.key} px={5} py={4} title={s.meta}>
                  <Text as="div" {...LABEL_STYLE} color={t.faint} mb="10px">
                    {s.label}
                  </Text>
                  {st.loading ? (
                    <Skeleton h="32px" w="48px" borderRadius={0} startColor={t.surface} endColor={t.ruleLight} />
                  ) : st.error ? (
                    <Text fontSize="13px" lineHeight="32px" title={st.error.message}>
                      Unavailable
                    </Text>
                  ) : (
                    <Heading as="div" fontSize="32px" lineHeight={1} fontWeight={600} color={s.key === "checksAwaiting" ? t.faint : t.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {value === null ? "—" : value}
                    </Heading>
                  )}
                  <Text fontSize="12px" color={t.muted} mt="8px">
                    {s.meta}
                  </Text>
                </Box>
              );
            })}
          </Grid>
        )}

        {/* Open roles + Verification queue */}
        {(canProspecting || canEmployees) && (
          <Grid templateColumns="repeat(auto-fit, minmax(min(100%, 460px), 1fr))" gap={8} alignItems="start">
            {canProspecting && (
              <Blueprint as="section">
                <PanelHeader spec={panelSpecs.openRoles} right={!formsQ.loading && !formsQ.error ? <Tag variant="accent" title="Forms currently accepting applicants">{derived.activeForms.length} active</Tag> : null} />
                <PanelDescription>{panelSpecs.openRoles.description}</PanelDescription>
                <PanelState loading={formsQ.loading && !formsQ.data} error={formsQ.error} empty={!formsQ.loading && forms.length === 0} emptyText="No screening forms yet. Create one in Prospecting → Forms." />
                {forms.length > 0 && (
                  <Box role="table" aria-label="Screening forms" pt={2}>
                    <Grid role="row" templateColumns="minmax(0, 1fr) auto" px={5} py={2} gap={3} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor={t.ruleLight}>
                      <ColumnHead>Role · client</ColumnHead>
                      <ColumnHead align="right">Status</ColumnHead>
                    </Grid>
                    {derived.formsSorted.slice(0, PANEL_ROW_LIMIT).map((f) => (
                      <Grid key={f.id} role="row" templateColumns="minmax(0, 1fr) auto" alignItems="center" px={5} py="11px" gap={3} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor={t.ruleLight} fontSize="14px">
                        <Box minW={0}>
                          <Text fontWeight={500} noOfLines={1}>
                            {f.jobTitle || f.name}
                          </Text>
                          <Text fontSize="13px" color={t.muted} noOfLines={1}>
                            {f.employerName || f.name}
                          </Text>
                        </Box>
                        <Tag variant={f.isActive ? "accent" : "neutral"} title={f.isActive ? "Accepting applicants" : "Not accepting applicants"}>
                          {f.isActive ? "Active" : "Inactive"}
                        </Tag>
                      </Grid>
                    ))}
                    <TruncationNote shown={Math.min(PANEL_ROW_LIMIT, forms.length)} total={forms.length} to={panelSpecs.openRoles.to} title={panelSpecs.openRoles.ctaTitle} />
                  </Box>
                )}
              </Blueprint>
            )}

            {canEmployees && (
              <Blueprint as="section">
                <PanelHeader
                  spec={panelSpecs.verification}
                  right={
                    employeesQ.loading && !employeesQ.data ? (
                      <Skeleton h="22px" w="72px" borderRadius={0} startColor={t.surface} endColor={t.ruleLight} />
                    ) : employeesQ.error ? (
                      <Tag variant="outline" title={employeesQ.error.message}>Count unavailable</Tag>
                    ) : typeof employeesTotal === "number" ? (
                      <Tag variant="neutral" title="Employee records on this tenant">{employeesTotal} on file</Tag>
                    ) : null
                  }
                />
                <PanelDescription>{panelSpecs.verification.description}</PanelDescription>
                <Box as="ul" listStyleType="none" m={0} px={5} py={4} display="flex" flexDirection="column" gap={2} fontSize="14px">
                  <Box as="li">
                    <Link as={RouterLink} to="/employees/onboarding-forms" title="Open onboarding forms in Employees (view only from here)" color={t.accent700}>
                      Onboarding forms →
                    </Link>
                  </Box>
                  <Box as="li">
                    <Link as={RouterLink} to="/employees/passkey-readiness" title="Open passkey readiness in Employees (view only from here)" color={t.accent700}>
                      Passkey readiness →
                    </Link>
                  </Box>
                  <Box as="li">
                    <Link as={RouterLink} to="/employees/invites" title="Open employee invites in Employees (view only from here)" color={t.accent700}>
                      Invites →
                    </Link>
                  </Box>
                </Box>
              </Blueprint>
            )}
          </Grid>
        )}

        {/* Shortlist */}
        {canProspecting && (
          <Blueprint as="section">
            <PanelHeader spec={panelSpecs.shortlist} right={!candidatesQ.loading && !candidatesQ.error ? <Tag variant="accent" title="Candidates at the shortlist stage">{derived.shortlisted.length} at shortlist</Tag> : null} />
            <PanelDescription>{panelSpecs.shortlist.description}</PanelDescription>
            <PanelState loading={candidatesQ.loading && !candidatesQ.data} error={candidatesQ.error} empty={!candidatesQ.loading && derived.shortlisted.length === 0} emptyText={candidates.length === 0 ? "No candidates yet." : "No candidates at the shortlist stage right now."} />
            {derived.shortlisted.length > 0 && (
              <Box role="table" aria-label="Shortlisted candidates" pt={2}>
                <Grid role="row" templateColumns={{ base: "40px minmax(0, 1fr) auto", md: "48px minmax(0, 1fr) minmax(0, 1fr) auto" }} px={5} py={2} gap={3} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor={t.ruleLight}>
                  <ColumnHead>Rank</ColumnHead>
                  <ColumnHead>Applicant</ColumnHead>
                  <Box display={{ base: "none", md: "block" }}>
                    <ColumnHead>Updated</ColumnHead>
                  </Box>
                  <ColumnHead align="right">Stage</ColumnHead>
                </Grid>
                {derived.shortlisted.slice(0, PANEL_ROW_LIMIT).map((c, i) => (
                  <Grid key={c.id} role="row" templateColumns={{ base: "40px minmax(0, 1fr) auto", md: "48px minmax(0, 1fr) minmax(0, 1fr) auto" }} alignItems="center" px={5} py="11px" gap={3} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor={t.ruleLight} fontSize="14px">
                    <Text fontSize="13px" fontWeight={600} letterSpacing=".08em" color={t.accent700}>
                      {String(i + 1).padStart(2, "0")}
                    </Text>
                    <Box minW={0}>
                      <Link as={RouterLink} to={`/prospecting/${c.id}`} title={`Open ${candidateName(c)} in Prospecting`} fontWeight={500} color={t.text} noOfLines={1} _hover={{ color: t.accent700 }}>
                        {candidateName(c)}
                      </Link>
                      <Text fontSize="13px" color={t.muted} noOfLines={1}>
                        {c.jobTitle || "Role not set"}
                        <Box as="span" display={{ base: "inline", md: "none" }}> · {whenLabel(c.updatedAt, now)}</Box>
                      </Text>
                    </Box>
                    <Text display={{ base: "none", md: "block" }} fontSize="13px" color={t.muted} sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {whenLabel(c.updatedAt, now)}
                    </Text>
                    <Tag variant={stageTag(c.stage)} title="Pipeline stage">{stageLabel(c.stage)}</Tag>
                  </Grid>
                ))}
                <TruncationNote shown={Math.min(PANEL_ROW_LIMIT, derived.shortlisted.length)} total={derived.shortlisted.length} to={panelSpecs.shortlist.to} title={panelSpecs.shortlist.ctaTitle} />
              </Box>
            )}
          </Blueprint>
        )}

        {/* Audit log */}
        {canProspecting && (
          <Box as="section">
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="baseline" gap={3} mb="12px">
              <Kicker noRule>Audit log · latest candidate updates</Kicker>
              <Link as={RouterLink} to={panelSpecs.audit.to} title={panelSpecs.audit.ctaTitle} fontSize="13px" color={t.accent700} _hover={{ color: t.steel, textDecoration: "none" }}>
                {panelSpecs.audit.ctaLabel}
              </Link>
            </Box>
            <Box h="1px" bg={t.rule} mb={1} />
            <Text fontSize="13px" color={t.muted} py={2}>
              {panelSpecs.audit.description}
            </Text>
            <PanelState loading={candidatesQ.loading && !candidatesQ.data} error={candidatesQ.error} empty={!candidatesQ.loading && candidates.length === 0} emptyText="No candidate activity yet." />
            {derived.byUpdated.slice(0, PANEL_ROW_LIMIT + 1).map((c) => (
              <Grid key={c.id} templateColumns={{ base: "minmax(0, 1fr) auto", md: "150px minmax(0, 1fr) auto" }} gap={{ base: 2, md: 4 }} py="10px" borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor={t.ruleLight} fontSize="13px" alignItems="center">
                <Text color={t.muted} sx={{ fontVariantNumeric: "tabular-nums" }} order={{ base: 3, md: 1 }} gridColumn={{ base: "1 / -1", md: "auto" }}>
                  {whenLabel(c.updatedAt, now)} · Prospecting
                </Text>
                <Text order={{ base: 1, md: 2 }} minW={0} noOfLines={2}>
                  Candidate record updated —{" "}
                  <Link as={RouterLink} to={`/prospecting/${c.id}`} title={`Open ${candidateName(c)} in Prospecting`} color={t.text} fontWeight={500} _hover={{ color: t.accent700 }}>
                    {candidateName(c)}
                  </Link>
                  {c.jobTitle ? `, ${c.jobTitle}` : ""}
                </Text>
                <Box order={{ base: 2, md: 3 }}>
                  <Tag variant={stageTag(c.stage)} title="Pipeline stage">{stageLabel(c.stage)}</Tag>
                </Box>
              </Grid>
            ))}
            <TruncationNote shown={Math.min(PANEL_ROW_LIMIT + 1, derived.byUpdated.length)} total={derived.byUpdated.length} to={panelSpecs.audit.to} title={panelSpecs.audit.ctaTitle} />
          </Box>
        )}

        {/* Nothing visible at all: say so instead of a blank page */}
        {!canProspecting && !canEmployees && visibleLinks.length === 0 && (
          <Blueprint p={8}>
            <Heading as="h2" fontSize="21px" lineHeight="24px" fontWeight={600} mb={2}>
              No modules to show
            </Heading>
            <Text fontSize="15px" lineHeight="22px" color={t.muted}>
              Your account has no module view permissions on this workspace yet. Ask a TalentLink+ administrator to grant access.
            </Text>
          </Blueprint>
        )}

        {/* HR services → real modules, ruled list */}
        {visibleLinks.length > 0 && (
          <Box as="section">
            <Kicker mb={6}>Also in this workspace</Kicker>
            <Box as="ul" listStyleType="none" m={0} p={0}>
              {visibleLinks.map((l, i) => (
                <Grid
                  as="li"
                  key={l.moduleId + l.to}
                  templateColumns={{ base: "minmax(0, 1fr) auto", md: "200px minmax(0, 1fr) auto" }}
                  alignItems="center"
                  gap={3}
                  py={4}
                  borderTopWidth="1px"
                  borderTopStyle="solid"
                  borderTopColor={t.ruleLight}
                  borderBottomWidth={i === visibleLinks.length - 1 ? "1px" : 0}
                  borderBottomStyle="solid"
                  borderBottomColor={t.ruleLight}
                >
                  <Heading as="h3" fontSize="20px" lineHeight="24px" fontWeight={600}>
                    {l.label}
                  </Heading>
                  <Text fontSize="15px" lineHeight="22px" color={t.muted} gridColumn={{ base: "1 / -1", md: "auto" }} order={{ base: 3, md: 2 }}>
                    {l.body}
                  </Text>
                  <Box order={{ base: 2, md: 3 }}>
                    <IndustryButton variant="ghost" to={l.to} title={l.title} px={2} minH="32px" fontSize="14px">
                      Open →
                    </IndustryButton>
                  </Box>
                </Grid>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};
